import { useQuery } from '@tanstack/react-query'
import { BookOpenText, ChevronDown, ChevronRight, Scale } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { api } from '@/lib/api/client'
import { useAuth } from '@/lib/auth/AuthProvider'
import { asList } from '@/lib/api/list'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../shared/format'
import { useJournal, useJournals } from './api'
import { ExportMenu } from './components/ExportMenu'
import { OpeningsPanel } from './components/OpeningsPanel'
import { OPENING_POST } from './pnl'
import { PeriodSelect, Section } from './components/FinanceBits'
import { downloadReport, exportTrialBalanceCsv, printTrialBalance, TRIAL_COLUMNS, type TrialBalance, type TrialRow } from './reports'
import { usePeriod } from './ui'

type Row = Record<string, unknown>
const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : '0.00')

const trialRow = (r: Row): TrialRow => ({
  code: String(r.code ?? ''),
  name: String(r.name ?? ''),
  level: Number(r.level) || 0,
  group: r.group === true,
  openingDebit: num(r.opening_debit),
  openingCredit: num(r.opening_credit),
  periodDebit: num(r.period_debit),
  periodCredit: num(r.period_credit),
  totalDebit: num(r.total_debit),
  totalCredit: num(r.total_credit),
  balanceDebit: num(r.balance_debit),
  balanceCredit: num(r.balance_credit),
})

function useTrialBalance(period: string) {
  return useQuery({
    queryKey: ['finance', 'trial-balance', period],
    queryFn: async (): Promise<TrialBalance> => {
      const r = await api.get<Row>('/api/v1/finance/trial-balance', { period })
      return { rows: asList(r.rows as Row[]).map(trialRow), totals: trialRow((r.totals ?? {}) as Row), balanced: r.balanced === true }
    },
  })
}

/** Muhasebeci görünümü: dönem mizanı (devir, dönem, toplam, bakiye; üst hesap toplamları) ve yevmiye kayıtları (ters kayıtlar dahil). */
export function LedgerBooksPage() {
  const { t } = useTranslation()
  const { month, setMonth } = usePeriod()
  const [tab, setTab] = useState<'trial' | 'journal' | 'openings'>('trial')
  const { can } = useAuth()
  const trial = useTrialBalance(month)
  const tb = trial.data
  const rows = tb?.rows ?? []
  const amount = (v: string) => (Number(v) ? fmtMoney(v) : '')

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.books.title')}
        description={t('finance.books.subtitle')}
        actions={
          <>
            <PeriodSelect value={month} onChange={setMonth} />
            {tab === 'trial' && tb ? <ExportMenu
                disabled={!rows.length}
                onExcel={() => downloadReport('trial-balance', 'xlsx', { period: month })}
                onCorporatePdf={() => downloadReport('trial-balance', 'pdf', { period: month })}
                onCsv={() => exportTrialBalanceCsv(tb, month)}
                onPdf={() => printTrialBalance(tb, month)}
              /> : null}
          </>
        }
      />
      <div className="space-y-4">
        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
          <TabsList>
            <TabsTrigger value="trial">{t('finance.books.trialTitle')}</TabsTrigger>
            <TabsTrigger value="journal">{t('finance.books.journalTitle')}</TabsTrigger>
            <TabsTrigger value="openings">{t('finance.openings.title')}</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === 'trial' ? (
          <Section title={t('finance.books.trialTitle')} icon={Scale} bodyClassName="p-0">
            {trial.isError ? (
              <ErrorState error={trial.error} onRetry={() => void trial.refetch()} />
            ) : trial.isLoading || !tb ? (
              <Skeleton className="m-4 h-48" />
            ) : !rows.length ? (
              <EmptyState icon={Scale} title={t('finance.books.empty')} />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <THead>
                    <TR>
                      <TH rowSpan={2}>{t('finance.books.code')}</TH>
                      <TH rowSpan={2}>{t('finance.books.account')}</TH>
                      {(['opening', 'period', 'total', 'balance'] as const).map((g) => (
                        <TH key={g} colSpan={2} className="border-l text-center">
                          {t(`finance.books.groups.${g}`)}
                        </TH>
                      ))}
                    </TR>
                    <TR>
                      {TRIAL_COLUMNS.map((c, i) => (
                        <TH key={c} className={cn('text-right', i % 2 === 0 && 'border-l')}>
                          {t(i % 2 === 0 ? 'finance.books.debit' : 'finance.books.credit')}
                        </TH>
                      ))}
                    </TR>
                  </THead>
                  <TBody>
                    {rows.map((r) => (
                      <TR key={r.code} className={cn(r.group && 'bg-muted/30 font-semibold')}>
                        <TD className="font-mono text-xs whitespace-nowrap">{r.code}</TD>
                        <TD className="min-w-48" style={{ paddingLeft: `${0.75 + r.level * 1.25}rem` }}>
                          {r.name}
                        </TD>
                        {TRIAL_COLUMNS.map((c, i) => (
                          <TD key={c} className={cn('text-right whitespace-nowrap tabular-nums', i % 2 === 0 && 'border-l')}>
                            {amount(r[c])}
                          </TD>
                        ))}
                      </TR>
                    ))}
                    <TR className="border-t-2 font-semibold">
                      <TD />
                      <TD>
                        <span className="flex items-center gap-2">
                          {t('finance.reports.totals')}
                          {tb.balanced ? <Badge tone="success">{t('finance.books.balanced')}</Badge> : <Badge tone="danger">{t('finance.books.unbalanced')}</Badge>}
                        </span>
                      </TD>
                      {TRIAL_COLUMNS.map((c, i) => (
                        <TD key={c} className={cn('text-right whitespace-nowrap tabular-nums', i % 2 === 0 && 'border-l')}>
                          {fmtMoney(tb.totals[c])}
                        </TD>
                      ))}
                    </TR>
                  </TBody>
                </Table>
              </div>
            )}
          </Section>
        ) : tab === 'openings' ? (
          <OpeningsPanel canPost={can(OPENING_POST)} />
        ) : (
          <JournalList period={month} />
        )}
      </div>
    </>
  )
}

const SOURCE_TONE: Record<string, 'warning' | 'neutral' | 'info'> = { reversal: 'warning', manual: 'info' }

function JournalList({ period }: { period: string }) {
  const { t } = useTranslation()
  const journals = useJournals(period)
  const [openId, setOpenId] = useState<string | null>(null)
  const rows = asList(journals.data as Row[] | undefined)
  return (
    <Section title={t('finance.books.journalTitle')} icon={BookOpenText} bodyClassName="p-0">
      {journals.isLoading ? (
        <Skeleton className="m-4 h-48" />
      ) : !rows.length ? (
        <EmptyState icon={BookOpenText} title={t('finance.books.noJournals')} />
      ) : (
        <ul className="divide-y">
          {rows.map((j) => {
            const id = String(j.id ?? '')
            const open = openId === id
            return (
              <li key={id}>
                <button type="button" aria-expanded={open} onClick={() => setOpenId(open ? null : id)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/40">
                  {open ? <ChevronDown className="size-4" aria-hidden /> : <ChevronRight className="size-4" aria-hidden />}
                  <span className="w-16 font-mono text-xs text-muted-foreground">#{String(j.entry_no ?? '')}</span>
                  <span className="w-24 text-sm">{fmtDate(String(j.entry_date ?? '').slice(0, 10))}</span>
                  <span className="min-w-0 flex-1 truncate text-sm">{String(j.description ?? '')}</span>
                  {j.reversed_by_entry_no ? <Badge tone="danger">{t('finance.books.reversedBy', { no: String(j.reversed_by_entry_no) })}</Badge> : null}
                  <Badge tone={SOURCE_TONE[String(j.source_type)] ?? 'neutral'}>{t(`finance.books.source.${String(j.source_type ?? '')}`, { defaultValue: t('finance.books.source.other') })}</Badge>
                </button>
                {open ? <JournalLines id={id} /> : null}
              </li>
            )
          })}
        </ul>
      )}
    </Section>
  )
}

function JournalLines({ id }: { id: string }) {
  const { t } = useTranslation()
  const journal = useJournal(id)
  const lines = asList(((journal.data as Row | undefined)?.lines ?? []) as Row[])
  // Kuruş hassasiyetinde toplam (kayan nokta hatası olmasın).
  const lineTotal = (k: 'debit' | 'credit') => (lines.reduce((n, l) => n + Math.round(Number(l[k] ?? 0) * 100), 0) / 100).toFixed(2)
  if (journal.isLoading) return <Skeleton className="mx-4 mb-3 h-16" />
  return (
    <div className="px-4 pb-3">
      <Table>
        <THead>
          <TR>
            <TH>{t('finance.books.account')}</TH>
            <TH className="text-right">{t('finance.books.debit')}</TH>
            <TH className="text-right">{t('finance.books.credit')}</TH>
          </TR>
        </THead>
        <TBody>
          {lines.map((l, i) => (
            <TR key={i}>
              <TD>
                <span className="font-mono text-xs text-muted-foreground">{String(l.gl_code ?? '')}</span> {String(l.gl_name ?? l.gl_account_id ?? '')}
              </TD>
              <TD className="text-right tabular-nums">{Number(l.debit) ? fmtMoney(num(l.debit)) : ''}</TD>
              <TD className="text-right tabular-nums">{Number(l.credit) ? fmtMoney(num(l.credit)) : ''}</TD>
            </TR>
          ))}
          <TR className="font-semibold">
            <TD>{t('finance.reports.totals')}</TD>
            <TD className="text-right tabular-nums">{fmtMoney(lineTotal('debit'))}</TD>
            <TD className="text-right tabular-nums">{fmtMoney(lineTotal('credit'))}</TD>
          </TR>
        </TBody>
      </Table>
    </div>
  )
}
